/**
 * Pure helpers for the "@" autocomplete — typing "@" in the chat message opens a dropdown of
 * blocks/columns/widgets on the page, the same `@block:<id>`/`@column:<id>`/`@widget:<id>` syntax
 * [[element-picker.util.ts]]'s click-to-pick flow already produces. Kept separate from
 * `entity-mention.service.ts` for the same reason as that file's own split: everything that touches
 * the store/translations is thin and untested, everything worth pinning down with a test is pure and
 * lives here.
 */
import type { PickedEntityType } from './element-picker.util';

/** One page entity offered in the dropdown — `label` is already resolved/translated text, ready to both filter against and display. */
export interface EntityMentionCandidate {
    type: PickedEntityType;
    id: string;
    label: string;
}

export interface MentionQuery {
    /** Index of the "@" that opened this mention — callers replace `[start, caretIndex)` on selection. */
    start: number;
    query: string;
}

const MENTION_TRIGGER = '@';
const MAX_CANDIDATES = 20;

/**
 * Finds the "@mention" being typed at the caret, if any — the standard boundary rule (GitHub, Slack,
 * …): the "@" must sit at the very start of the message or right after whitespace, and nothing
 * whitespace-shaped may appear between it and the caret. This is what keeps "email me at
 * name@example.com" from popping the dropdown open on the "@".
 */
export function detectMentionQuery(text: string, caretIndex: number): MentionQuery | null {
    const uptoCaret = text.slice(0, caretIndex);
    const start = uptoCaret.lastIndexOf(MENTION_TRIGGER);

    if (start === -1) {
        return null;
    }

    const charBefore = start > 0 ? uptoCaret[start - 1] : '';

    if (charBefore && !/\s/.test(charBefore)) {
        return null;
    }

    const query = uptoCaret.slice(start + 1);

    if (/\s/.test(query)) {
        return null;
    }

    return { start, query };
}

/**
 * Matches the typed query against a candidate's resolved label, its type name, or a prefix of its
 * raw id (useful when the query is a pasted id fragment) — capped well below "every widget on a
 * large page" so the dropdown stays scannable.
 */
export function filterEntityMentionCandidates(
    candidates: readonly EntityMentionCandidate[],
    query: string,
    limit = MAX_CANDIDATES
): EntityMentionCandidate[] {
    if (!query) {
        return candidates.slice(0, limit);
    }

    const needle = query.toLowerCase();

    return candidates
        .filter(
            (candidate) =>
                candidate.label.toLowerCase().includes(needle) ||
                candidate.type.includes(needle) ||
                candidate.id.toLowerCase().startsWith(needle)
        )
        .slice(0, limit);
}
